import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { MotionRoot } from '@/components/generals/motion-root'
import { PageHero } from '@/components/generals/page-hero'
import { PageIntro } from '@/components/generals/page-intro'
import { InfoGrid } from '@/components/generals/info-grid'
import { FaqSection } from '@/components/generals/faq-section'
import { NextPage } from '@/components/generals/next-page'
import { AcademySection } from '@/components/landing/academy-section'
import { AcademyProgrammes } from '@/components/subpages/academy-programmes'

import {
    academyFaqs,
    academyIntroStats,
    nextPageAfter,
    pages,
    studioSteps
} from '@/constants/pages'

const page = pages.academy
const next = nextPageAfter('academy')

const AcademyPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo title={page.seoTitle} description={page.description} ogImage={page.image.src} />

            <MotionRoot>
                <PageHero
                    index={page.index}
                    eyebrow={page.eyebrow}
                    title={page.title}
                    accent={page.accent}
                    image={page.image}
                    facts={page.facts}
                    next='academy-intro'
                />

                <PageIntro
                    id='academy-intro'
                    index='01'
                    label='Practice with purpose'
                    aside='Range · Short game · Putting · Studio'
                    statement={<>Every swing leaves data behind. <em>We turn it into a plan</em>, and the plan into lower scores.</>}
                    body='The Academy is run by eleven PGA professionals around one idea: practice should feel like golf. Every bay, green and bunker is tracked, and every session ends with a clear next step sent to your phone.'
                    stats={academyIntroStats}
                />

                <AcademySection index='02' />

                <AcademyProgrammes index='03' />

                <InfoGrid
                    id='studio-session'
                    index='04'
                    label='The Performance Studio'
                    heading={<>An hour that changes <em>how you practise.</em></>}
                    intro='Motion capture, launch data and a coach who knows which three numbers matter for your game.'
                    items={studioSteps}
                    columns={4}
                    steps
                />

                <FaqSection
                    id='academy-questions'
                    index='05'
                    label='Questions'
                    heading={<>Before <em>your first session.</em></>}
                    intro='Anything else, the Academy team is on the range from half past six every morning.'
                    items={academyFaqs}
                />

                <NextPage
                    href={next.href}
                    index={next.index}
                    title={next.title}
                    teaser={next.teaser}
                    image={next.image}
                />
            </MotionRoot>
        </Layout>
    )
}

export default AcademyPage
